import { ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { Alert, Button, Card, Form, Table } from 'react-bootstrap';

import {
  AppInformationQuery,
  CurrencyUnlimitedBoostType,
  GachaAmountMode,
  GachaRewardType,
  GetVanityPiecesQuery,
} from '../../../../../../__gqltypes__';
import { APP_INFORMATION } from '../../../../devtools/graphql';
// Vanity Pieces' own catalog query and type labels, read-only.
import { GET_VANITY_PIECES } from '../../../components/apolloQueries';
import { VANITY_PIECE_TYPE_LABELS } from '../../Items/utils';
import {
  formatChance,
  GACHA_BOOST_TYPES,
  GACHA_REWARD_TYPE_LABELS,
  GACHA_REWARD_TYPES,
  GACHA_UNLIMITED_BOOST_TYPES,
  gachaChancePercents,
  GachaCountRowDraft,
  GachaPoolRowDraft,
  GachaRewardDraft,
  newGachaCountRow,
  newGachaPoolRow,
  reorderList,
} from '../utils';
import { gachaCountWeightTotal, validateGachaCountRows, validateGachaPoolRows } from '../validation';

type CommonProps = {
  // Stable, unique id (drag context + form controls).
  tableId: string;
  title: string;
  hint?: string;
  // Rule errors that involve more than this table (e.g. the count list against the pool size).
  extraErrors?: string[];
  // Template controls (Save as template / Load template), shown in the header.
  headerExtra?: ReactNode;
};

type CountProps = CommonProps & {
  variant: 'count';
  rows: GachaCountRowDraft[];
  onChange: (rows: GachaCountRowDraft[]) => void;
};

type PoolProps = CommonProps & {
  variant: 'rewards' | 'mystery';
  rows: GachaPoolRowDraft[];
  onChange: (rows: GachaPoolRowDraft[]) => void;
};

export type WeightedRewardTableProps = CountProps | PoolProps;

// Narrows the props union by its variant (TypeScript 4.5 does not narrow a destructured union).
const isCountTable = (tableProps: WeightedRewardTableProps): tableProps is CountProps => tableProps.variant === 'count';

const toInteger = (raw: string): number => {
  const value = Number(raw);
  return Number.isFinite(value) ? Math.floor(value) : 0;
};
const toNumber = (raw: string): number => {
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
};

// The other half of a reward: the field that names the item for its type.
type GachaVanityPieceOption = GetVanityPiecesQuery['liveOps']['vanityPieces'][number];

function RewardItemControl({
  reward,
  onChange,
  categories,
  vanityPieces,
}: {
  reward: GachaRewardDraft;
  onChange: (reward: GachaRewardDraft) => void;
  categories: Array<{ id: string; name: string }>;
  vanityPieces: Array<GachaVanityPieceOption>;
}): JSX.Element | null {
  switch (reward.type) {
    case GachaRewardType.CURRENCY_BOOST:
      return (
        <Form.Control
          as="select"
          size="sm"
          aria-label="Booster type"
          value={reward.itemId ?? ''}
          isInvalid={!reward.itemId}
          onChange={(e) => onChange({ ...reward, itemId: e.target.value || null })}
        >
          <option value="">Booster type…</option>
          {GACHA_BOOST_TYPES.map((boost) => (
            <option key={boost} value={boost}>
              {boost}
            </option>
          ))}
        </Form.Control>
      );
    case GachaRewardType.CURRENCY_UNLIMITED_BOOST:
      return (
        <Form.Control
          as="select"
          size="sm"
          aria-label="Unlimited booster type"
          value={reward.unlimitedBoostType ?? ''}
          isInvalid={!reward.unlimitedBoostType}
          onChange={(e) =>
            onChange({ ...reward, unlimitedBoostType: (e.target.value || null) as CurrencyUnlimitedBoostType | null })
          }
        >
          <option value="">Unlimited booster type…</option>
          {GACHA_UNLIMITED_BOOST_TYPES.map((boost) => (
            <option key={boost} value={boost}>
              {boost}
            </option>
          ))}
        </Form.Control>
      );
    case GachaRewardType.CURRENCY_CATEGORY_FRAGMENT: {
      const known = categories.some((category) => category.id === reward.itemId);
      return (
        <Form.Control
          as="select"
          size="sm"
          aria-label="Fragment category"
          value={reward.itemId ?? ''}
          isInvalid={!reward.itemId}
          onChange={(e) => onChange({ ...reward, itemId: e.target.value || null })}
        >
          <option value="">Category…</option>
          {reward.itemId && !known && <option value={reward.itemId}>{reward.itemId}</option>}
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {`${category.name} (${category.id})`}
            </option>
          ))}
        </Form.Control>
      );
    }
    case GachaRewardType.CURRENCY_VANITY_PIECE: {
      // The piece type (one catalog entry per set of pieces); its id is what the row stores.
      const known = vanityPieces.some((piece) => piece.id === reward.itemId);
      return (
        <Form.Control
          as="select"
          size="sm"
          aria-label="Vanity piece type"
          value={reward.itemId ?? ''}
          isInvalid={!reward.itemId}
          onChange={(e) => onChange({ ...reward, itemId: e.target.value || null })}
        >
          <option value="">Vanity piece…</option>
          {reward.itemId && !known && <option value={reward.itemId}>{reward.itemId}</option>}
          {vanityPieces.map((piece) => (
            <option key={piece.id} value={piece.id}>
              {piece.type ? `${piece.name} (${VANITY_PIECE_TYPE_LABELS[piece.type]})` : piece.name}
            </option>
          ))}
        </Form.Control>
      );
    }
    case GachaRewardType.CONTENT_PACK_REWARD:
      return (
        <Form.Control
          size="sm"
          aria-label="Playlist id"
          placeholder="Playlist (content pack) id"
          value={reward.itemId ?? ''}
          isInvalid={!(reward.itemId ?? '').trim()}
          onChange={(e) => onChange({ ...reward, itemId: e.target.value })}
        />
      );
    default:
      return null;
  }
}

function CountRows({ rows, onChange }: { rows: GachaCountRowDraft[]; onChange: CountProps['onChange'] }) {
  const chances = gachaChancePercents(rows.map((row) => row.weight));
  const updateRow = (index: number, patch: Partial<GachaCountRowDraft>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  return (
    <>
      {rows.map((row, index) => (
        <Draggable key={row._clientId} draggableId={row._clientId} index={index}>
          {(draggable) => (
            <tr ref={draggable.innerRef} {...draggable.draggableProps}>
              <td className="align-middle text-center" {...draggable.dragHandleProps} title="Drag to reorder">
                <i className="fas fa-grip-vertical text-muted mr-1" />
                {index + 1}
              </td>
              <td className="align-middle">
                <Form.Control
                  size="sm"
                  type="number"
                  min={1}
                  step={1}
                  aria-label="Reward count"
                  value={row.count}
                  isInvalid={!Number.isInteger(row.count) || row.count < 1}
                  onChange={(e) => updateRow(index, { count: toInteger(e.target.value) })}
                />
              </td>
              <td className="align-middle">
                <Form.Control
                  size="sm"
                  type="number"
                  min={0}
                  step="any"
                  aria-label="Weight"
                  value={row.weight}
                  isInvalid={!(row.weight >= 0)}
                  onChange={(e) => updateRow(index, { weight: toNumber(e.target.value) })}
                />
              </td>
              <td className="align-middle text-center">{formatChance(chances[index])}</td>
              <td className="align-middle text-center">
                <Button
                  size="sm"
                  variant="outline-danger"
                  title="Remove row"
                  onClick={() => onChange(rows.filter((_, i) => i !== index))}
                >
                  <i className="fas fa-trash" />
                </Button>
              </td>
            </tr>
          )}
        </Draggable>
      ))}
    </>
  );
}

function PoolRows({
  tableId,
  rows,
  onChange,
  categories,
  vanityPieces,
}: {
  tableId: string;
  rows: GachaPoolRowDraft[];
  onChange: PoolProps['onChange'];
  categories: Array<{ id: string; name: string }>;
  vanityPieces: Array<GachaVanityPieceOption>;
}) {
  const chances = gachaChancePercents(rows.map((row) => row.weight));
  const updateRow = (index: number, patch: Partial<GachaPoolRowDraft>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  return (
    <>
      {rows.map((row, index) => {
        return (
          <Draggable key={row._clientId} draggableId={row._clientId} index={index}>
            {(draggable) => (
              <tr ref={draggable.innerRef} {...draggable.draggableProps}>
                <td className="align-middle text-center" {...draggable.dragHandleProps} title="Drag to reorder">
                  <i className="fas fa-grip-vertical text-muted mr-1" />
                  {index + 1}
                </td>
                <td className="align-middle">
                  <Form.Control
                    as="select"
                    size="sm"
                    id={`${tableId}-${row._clientId}-type`}
                    aria-label="Reward type"
                    value={row.reward.type}
                    onChange={(e) =>
                      updateRow(index, {
                        reward: { type: e.target.value as GachaRewardType, itemId: null, unlimitedBoostType: null },
                      })
                    }
                  >
                    {GACHA_REWARD_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {GACHA_REWARD_TYPE_LABELS[type]}
                      </option>
                    ))}
                  </Form.Control>
                  <div className="mt-1">
                    <RewardItemControl
                      reward={row.reward}
                      categories={categories}
                      vanityPieces={vanityPieces}
                      onChange={(reward) => updateRow(index, { reward })}
                    />
                  </div>
                </td>
                <td className="align-middle">
                  {/* Explicit per-row toggle (PRA's pattern): Exact and Range rows mix freely in one table. */}
                  <Form.Check
                    inline
                    type="radio"
                    id={`${tableId}-${row._clientId}-exact`}
                    name={`${tableId}-${row._clientId}-mode`}
                    label="Exact"
                    checked={row.amountMode === GachaAmountMode.EXACT}
                    onChange={() => updateRow(index, { amountMode: GachaAmountMode.EXACT })}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id={`${tableId}-${row._clientId}-range`}
                    name={`${tableId}-${row._clientId}-mode`}
                    label="Range"
                    checked={row.amountMode === GachaAmountMode.RANGE}
                    onChange={() => updateRow(index, { amountMode: GachaAmountMode.RANGE })}
                  />
                </td>
                <td className="align-middle">
                  {row.amountMode === GachaAmountMode.EXACT ? (
                    <Form.Control
                      size="sm"
                      type="number"
                      min={1}
                      step={1}
                      aria-label="Exact amount"
                      value={row.amount}
                      isInvalid={!Number.isInteger(row.amount) || row.amount < 1}
                      onChange={(e) => updateRow(index, { amount: toInteger(e.target.value) })}
                    />
                  ) : (
                    <div className="d-flex align-items-center">
                      <Form.Control
                        size="sm"
                        type="number"
                        min={1}
                        step={1}
                        aria-label="Min amount"
                        value={row.minAmount}
                        isInvalid={!Number.isInteger(row.minAmount) || row.minAmount < 1}
                        onChange={(e) => updateRow(index, { minAmount: toInteger(e.target.value) })}
                      />
                      <span className="mx-1 text-muted">–</span>
                      <Form.Control
                        size="sm"
                        type="number"
                        min={1}
                        step={1}
                        aria-label="Max amount"
                        value={row.maxAmount}
                        isInvalid={!Number.isInteger(row.maxAmount) || row.maxAmount < row.minAmount}
                        onChange={(e) => updateRow(index, { maxAmount: toInteger(e.target.value) })}
                      />
                    </div>
                  )}
                </td>
                <td className="align-middle">
                  <Form.Control
                    size="sm"
                    type="number"
                    min={0}
                    step="any"
                    aria-label="Weight"
                    value={row.weight}
                    isInvalid={!(row.weight > 0)}
                    onChange={(e) => updateRow(index, { weight: toNumber(e.target.value) })}
                  />
                </td>
                <td className="align-middle text-center">{formatChance(chances[index])}</td>
                <td className="align-middle text-center">
                  <Button
                    size="sm"
                    variant="outline-danger"
                    title="Remove row"
                    onClick={() => onChange(rows.filter((_, i) => i !== index))}
                  >
                    <i className="fas fa-trash" />
                  </Button>
                </td>
              </tr>
            )}
          </Draggable>
        );
      })}
    </>
  );
}

/**
 * One weighted table, three variants:
 * - `count`: the reward-count list, a number of rewards + weight (a weight of 0 never comes up);
 * - `rewards`: the box reward table; `mystery`: the mystery reward table. Both are reward + min/max amount + weight.
 * Every variant shows the live derived chance (weight / total, as the server derives it; for the reward tables the
 * first selection's chance).
 * List order = display order (drag the # handle). Validation mirrors the server's.
 */
export function WeightedRewardTable(props: WeightedRewardTableProps): JSX.Element {
  const { tableId, title, hint, extraErrors = [], headerExtra, variant, rows } = props;
  // Runs the branch of the props variant, with its rows/onChange typed for that variant.
  const byVariant = <T,>(onCount: (count: CountProps) => T, onPool: (pool: PoolProps) => T): T =>
    isCountTable(props) ? onCount(props) : onPool(props);

  const needsCategories = byVariant(
    () => false,
    (pool) => pool.rows.some((row) => row.reward.type === GachaRewardType.CURRENCY_CATEGORY_FRAGMENT)
  );
  const { data: appData } = useQuery<AppInformationQuery>(APP_INFORMATION, { skip: !needsCategories });
  const needsVanityPieces = byVariant(
    () => false,
    (pool) => pool.rows.some((row) => row.reward.type === GachaRewardType.CURRENCY_VANITY_PIECE)
  );
  const { data: vanityData } = useQuery<GetVanityPiecesQuery>(GET_VANITY_PIECES, { skip: !needsVanityPieces });
  const vanityPieces = vanityData?.liveOps.vanityPieces ?? [];
  const categories = (appData?.app.contentCategories ?? []).map((category) => ({
    id: category.id,
    name: category.name,
  }));

  const tableErrors = byVariant(
    (count) => validateGachaCountRows(count.rows, title),
    (pool) => validateGachaPoolRows(pool.rows, title)
  );
  const errors = [...tableErrors, ...extraErrors];

  const onDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }
    const fromIndex = result.source.index;
    const toIndex = result.destination.index;
    byVariant(
      (count) => count.onChange(reorderList(count.rows, fromIndex, toIndex)),
      (pool) => pool.onChange(reorderList(pool.rows, fromIndex, toIndex))
    );
  };

  const totalWeight = byVariant(
    (count) => gachaCountWeightTotal(count.rows),
    (pool) => pool.rows.reduce((sum, row) => sum + (Number.isFinite(row.weight) ? row.weight : 0), 0)
  );

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <strong>{title}</strong> {hint && <span className="text-muted small">{hint}</span>}
        </div>
        {headerExtra}
      </Card.Header>
      <Card.Body>
        <DragDropContext onDragEnd={onDragEnd}>
          <Table bordered responsive size="sm" className="mb-2">
            <thead className="text-center">
              {variant === 'count' ? (
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>Number of rewards</th>
                  <th style={{ width: 110 }}>Weight</th>
                  <th style={{ width: 90 }}>Chance</th>
                  <th style={{ width: 50 }} />
                </tr>
              ) : (
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>Reward</th>
                  <th style={{ width: 150 }}>Mode</th>
                  <th style={{ width: 200 }}>Amount</th>
                  <th style={{ width: 110 }}>Weight</th>
                  <th style={{ width: 90 }}>Chance</th>
                  <th style={{ width: 50 }} />
                </tr>
              )}
            </thead>
            <Droppable droppableId={tableId}>
              {(droppable) => (
                <tbody ref={droppable.innerRef} {...droppable.droppableProps}>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={variant === 'count' ? 5 : 7} className="text-center text-muted">
                        The table needs at least one row.
                      </td>
                    </tr>
                  )}
                  {byVariant(
                    (count) => (
                      <CountRows rows={count.rows} onChange={count.onChange} />
                    ),
                    (pool) => (
                      <PoolRows
                        tableId={tableId}
                        rows={pool.rows}
                        onChange={pool.onChange}
                        categories={categories}
                        vanityPieces={vanityPieces}
                      />
                    )
                  )}
                  {droppable.placeholder}
                </tbody>
              )}
            </Droppable>
          </Table>
        </DragDropContext>
        <div className="d-flex justify-content-between align-items-center">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => {
              byVariant(
                (count) => {
                  const nextCount = Math.max(0, ...count.rows.map((row) => row.count)) + 1;
                  count.onChange([...count.rows, newGachaCountRow(nextCount, 0)]);
                },
                (pool) => pool.onChange([...pool.rows, newGachaPoolRow()])
              );
            }}
          >
            <i className="fas fa-plus mr-1" />
            Add row
          </Button>
          <span className="small text-muted">
            {variant === 'count'
              ? `Total weight: ${totalWeight}. A row with weight 0 never comes up.`
              : `Total weight: ${totalWeight}. Exact grants the amount; Range rolls a whole number between min and max.`}
          </span>
        </div>
        {errors.length > 0 && (
          <Alert variant="warning" className="mt-2 mb-0 py-2 small">
            <ul className="mb-0 pl-3">
              {errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
}
